import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useSearchParams } from "react-router-dom"

import {
  getTableSearchParams,
  usePaginationState,
  useTableSearchTextState,
  useTableSort,
} from "../../../common/components/tables/utils"
import { useNavigateUpdatedSearchParams } from "../../../common/utils"

export function normalizeStaffListResponse(data) {
  if (Array.isArray(data)) {
    return { count: data.length, results: data }
  }
  return {
    count: data?.count ?? 0,
    results: data?.results ?? [],
  }
}

export function formatAdminLoadError(error, fallback = "Could not load data.") {
  const status = error?.response?.status
  const detail = error?.response?.data?.detail

  if (status === 404) {
    return "This admin API is not available on the server yet. The backend must be redeployed with the trial management endpoints before data can load here."
  }
  if (status === 403) {
    return "You do not have staff permission to view this data."
  }
  if (status === 401) {
    return "Your admin session expired. Log out and sign in again."
  }
  if (typeof detail === "string" && detail.trim()) {
    return detail
  }
  return fallback
}

export function paginateDemoList(list, params = {}) {
  const page = Number(params.page) || 1
  const pageSize = Number(params.page_size) || 20
  const q = String(params.q || params.search || "")
    .toLowerCase()
    .trim()

  let filtered = [...list]

  if (params.status && params.status !== "all") {
    filtered = filtered.filter((item) => item.status === params.status)
  }
  if (params.approval_status && params.approval_status !== "all") {
    filtered = filtered.filter(
      (item) => item.approval_status === params.approval_status
    )
  }
  if (params.is_concierge === "true") {
    filtered = filtered.filter((item) => item.is_concierge)
  } else if (params.is_concierge === "false") {
    filtered = filtered.filter((item) => !item.is_concierge)
  }
  if (params.event_type) {
    filtered = filtered.filter((item) => item.event_type === params.event_type)
  }
  if (params.concierge_user_id) {
    filtered = filtered.filter(
      (item) =>
        String(item.concierge_user_id) === String(params.concierge_user_id) ||
        String(item.id) === String(params.concierge_user_id) ||
        String(item.performed_by_id) === String(params.concierge_user_id) ||
        String(item.assigned_user_id) === String(params.concierge_user_id)
    )
  }
  if (params.concierge) {
    const conciergeTerm = String(params.concierge).toLowerCase().trim()
    filtered = filtered.filter((item) =>
      [item.concierge_name, item.concierge_email, item.name, item.email]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(conciergeTerm))
    )
  }

  if (q) {
    filtered = filtered.filter((item) =>
      JSON.stringify(item).toLowerCase().includes(q)
    )
  }

  const start = (page - 1) * pageSize
  return {
    count: filtered.length,
    results: filtered.slice(start, start + pageSize),
  }
}

/**
 * Filter specs for `useAdminPaginatedQuery({filters})`: key -> {
 *   urlKey   short URL key (defaults to key),
 *   param    backend query param (defaults to key),
 *   default  value when the URL has nothing ("" = no filter),
 *   toParams optional (value) -> {param: value} for values that map to
 *            several params or need translating (e.g. "has" -> has_mls=true).
 * }
 * Values are always strings (multi-selects are comma lists), so they round-trip
 * through the URL unchanged. A value equal to the default is never written to
 * the URL, which keeps the default view's URL clean.
 */
export function readFilterValues(filterSpecs, searchParams) {
  const values = {}
  Object.entries(filterSpecs || {}).forEach(([key, spec]) => {
    const raw = searchParams.get(spec.urlKey || key)
    values[key] = raw == null ? spec.default ?? "" : raw
  })
  return values
}

export function filterValuesToParams(filterSpecs, values) {
  const params = {}
  Object.entries(filterSpecs || {}).forEach(([key, spec]) => {
    const value = values[key] ?? ""
    if (spec.toParams) {
      Object.assign(params, spec.toParams(value) || {})
    } else if (value !== "") {
      params[spec.param || key] = value
    }
  })
  return params
}

export function countActiveFilters(filterSpecs, values) {
  return Object.entries(filterSpecs || {}).filter(
    ([key, spec]) => (values[key] ?? "") !== (spec.default ?? "")
  ).length
}

export function useAdminPaginatedQuery(
  fetchFn,
  {
    extraParams = {},
    filters: filterSpecs = null,
    defaultSortColumn = null,
    defaultSortDirection = "asc",
    defaultPageSize = 20,
    enabled = true,
  } = {}
) {
  const {
    pageIndex,
    setPageIndex,
    totalResultsSize,
    setTotalResultsSize,
    pageSize,
    setPageSize,
  } = usePaginationState(defaultPageSize)
  const [searchText, setSearchText, debouncedSearchText, setSearchTextOnly] =
    useTableSearchTextState("q", setPageIndex)
  const [searchParams] = useSearchParams()
  const navigateUpdatedSearchParams = useNavigateUpdatedSearchParams()
  const filterSpecsKey = JSON.stringify(
    Object.entries(filterSpecs || {}).map(([key, spec]) => [
      key,
      spec.urlKey || key,
      spec.default ?? "",
    ])
  )
  const filterUrlState = Object.entries(filterSpecs || {})
    .map(([key, spec]) => `${key}=${searchParams.get(spec.urlKey || key)}`)
    .join("&")
  // The URL is the source of truth for filters; recompute only when the
  // filter keys in it change so pagination/sort writes don't churn params.
  const filterValues = useMemo(
    () => readFilterValues(filterSpecs, searchParams),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filterSpecsKey, filterUrlState]
  )
  const filterParams = useMemo(
    () => filterValuesToParams(filterSpecs, filterValues),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filterSpecsKey, filterValues]
  )
  const activeFilterCount = countActiveFilters(filterSpecs, filterValues)

  const setFilter = useCallback(
    (key, value) => {
      const spec = filterSpecs?.[key]
      if (!spec) return
      const next = value == null ? "" : String(value)
      const isDefault = next === (spec.default ?? "")
      // A non-empty default ("active only" ON) needs an explicit value in the
      // URL when cleared, otherwise reading the URL brings the default back.
      navigateUpdatedSearchParams({
        [spec.urlKey || key]: isDefault ? null : next === "" ? "all" : next,
      })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filterSpecsKey, navigateUpdatedSearchParams]
  )

  // "Clear all": every filter back to its default and the search box emptied,
  // in a single navigate.
  const clearFilters = useCallback(() => {
    const cleared = { q: null, pi: 1 }
    Object.entries(filterSpecs || {}).forEach(([key, spec]) => {
      cleared[spec.urlKey || key] = null
    })
    setSearchTextOnly("")
    setPageIndex(1, false)
    navigateUpdatedSearchParams(cleared)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterSpecsKey, navigateUpdatedSearchParams, setSearchTextOnly])
  const { sortColumn, sortDirection, onSortColumnChanged, serverSort } =
    useTableSort(defaultSortColumn, defaultSortDirection)
  const [rows, setRows] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [loadError, setLoadError] = useState(null)
  const [reloadToken, setReloadToken] = useState(0)
  const extraParamsKey = useMemo(
    () => JSON.stringify({ ...filterParams, ...(extraParams || {}) }),
    [extraParams, filterParams]
  )
  const stableExtraParams = useMemo(
    () => JSON.parse(extraParamsKey),
    [extraParamsKey]
  )

  // Filtering is server-side, so a filter change invalidates the current page:
  // staying on page 3 asks for a slice that no longer exists and renders an
  // empty table while the footer still reports matching rows. Search and sort
  // already reset the page; this does the same for filters.
  const previousExtraParamsKeyRef = useRef(extraParamsKey)
  useEffect(() => {
    if (previousExtraParamsKeyRef.current === extraParamsKey) {
      return
    }
    previousExtraParamsKeyRef.current = extraParamsKey
    setPageIndex(1)
  }, [extraParamsKey, setPageIndex])

  // Active search/sort/filter params without pagination, for exports.
  const exportParams = useMemo(
    () => ({
      q: debouncedSearchText,
      ordering: serverSort,
      ...stableExtraParams,
    }),
    [debouncedSearchText, serverSort, stableExtraParams]
  )

  const refresh = useCallback(
    ({ resetPage = false } = {}) => {
      if (resetPage) {
        setPageIndex(1)
      }
      setReloadToken((token) => token + 1)
    },
    [setPageIndex]
  )

  useEffect(() => {
    if (!enabled) {
      return undefined
    }
    let cancelled = false
    setIsLoading(true)
    setLoadError(null)
    const params = {
      ...getTableSearchParams(
        debouncedSearchText,
        pageIndex,
        pageSize,
        serverSort
      ),
      ...stableExtraParams,
    }
    fetchFn(params)
      .then(({ data }) => {
        if (cancelled) return
        const normalized = normalizeStaffListResponse(data)
        setRows(normalized.results)
        setTotalResultsSize(normalized.count)
      })
      .catch((error) => {
        if (cancelled) return
        setRows([])
        setTotalResultsSize(0)
        setLoadError(formatAdminLoadError(error))
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false)
        }
      })
    return () => {
      cancelled = true
    }
  }, [
    debouncedSearchText,
    enabled,
    extraParamsKey,
    fetchFn,
    pageIndex,
    pageSize,
    reloadToken,
    serverSort,
    setTotalResultsSize,
    stableExtraParams,
  ])

  return {
    rows,
    setRows,
    isLoading,
    pageIndex,
    setPageIndex,
    pageSize,
    setPageSize,
    totalResultsSize,
    setTotalResultsSize,
    searchText,
    setSearchText,
    sortColumn,
    sortDirection,
    onSortColumnChanged,
    refresh,
    loadError,
    exportParams,
    filters: filterValues,
    filterSpecs,
    setFilter,
    clearFilters,
    activeFilterCount,
  }
}
